// Minimal hash router. Each view is a factory that renders into a container and
// returns a teardown function. Routes support one `:param` segment, and a
// `?key=value` query string whose pairs land in the same `params` bag.

import { t } from '../strings';
import { el } from './dom';

export interface ViewContext {
  params: Record<string, string>;
  navigate: (path: string) => void;
  /**
   * The path this view was reached from, or '' on a cold load.
   *
   * A view uses it to tell a return visit from a fresh one — the Library
   * restores the filter and scroll position it had when the visit that comes
   * back from a species page left it, and starts clean for anyone arriving from
   * the tab bar.
   */
  from: string;
  /**
   * Leave this view the way the learner arrived at it: back through history
   * when there is app history to go back through, and to `fallback` otherwise.
   *
   * A hardcoded destination is wrong the moment a screen has more than one way
   * in. The species page is reachable from the Library, from a quiz's list of
   * misses and from a shared link, and "back" that always means /library throws
   * away a filtered list on the first of those and invents a history on the last.
   */
  back: (fallback: string) => void;
  /**
   * Go somewhere without leaving a step behind.
   *
   * For a screen that walks sideways through a list — the species page's
   * previous/next — where every step is a fresh render of the same screen
   * rather than a place the learner chose to go. Ordinary navigation would
   * stack one history entry per bird, so backing out of a browse through a
   * family means pressing back eleven times to reach the Library you came from.
   */
  replace: (path: string) => void;
  /**
   * Say where the view has got to, without re-rendering it.
   *
   * `replace` re-enters the view, which is right when the step IS a fresh
   * render — the species page draws a different bird. It is wrong for a view
   * that repaints itself in place and only wants the address bar to agree: the
   * side-by-side view redraws one of its two panes on a swipe, and a `replace`
   * would tear the whole surface down and rebuild both, losing the other
   * pane's place in its own photo strip to record a fact the view had already
   * acted on.
   *
   * The router's own idea of the current path moves with it, so the next
   * view's `from` and the back guard stay true.
   */
  rewrite: (path: string) => void;
}

/** A view renders into `root` and returns an optional cleanup function. */
export type View = (
  root: HTMLElement,
  ctx: ViewContext,
) => void | (() => void) | Promise<void | (() => void)>;

/**
 * Split `/quiz/sound?done=amerob` into its path and its query pairs.
 *
 * The query is how one view hands the next a fact about the visit rather than
 * about the destination — the daily card telling the Sound ID round which bird
 * it has already asked. Keeping it in the hash means a reload carries it, so
 * the round doesn't silently grow a question back.
 */
export function splitQuery(path: string): {
  path: string;
  query: Record<string, string>;
} {
  const at = path.indexOf('?');
  if (at < 0) return { path, query: {} };
  const query: Record<string, string> = {};
  for (const pair of path.slice(at + 1).split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const key = decodeURIComponent(eq < 0 ? pair : pair.slice(0, eq));
    query[key] = eq < 0 ? '' : decodeURIComponent(pair.slice(eq + 1));
  }
  return { path: path.slice(0, at), query };
}

interface Route {
  pattern: string;
  segments: string[];
  view: View;
}

export class Router {
  private routes: Route[] = [];
  private teardown: (() => void) | void = undefined;
  private currentPath = '';
  private previousPath = '';

  constructor(private outlet: HTMLElement) {}

  add(pattern: string, view: View): this {
    this.routes.push({
      pattern,
      segments: pattern.split('/').filter(Boolean),
      view,
    });
    return this;
  }

  start(fallback = '/home'): void {
    const onChange = () => {
      const path = location.hash.replace(/^#/, '') || fallback;
      void this.resolve(path);
    };
    window.addEventListener('hashchange', onChange);
    if (!location.hash) location.hash = fallback;
    else onChange();
  }

  navigate(path: string, replace = false): void {
    const hash = '#' + path;
    if (hash === location.hash) void this.resolve(path);
    else if (replace) {
      // replaceState doesn't fire hashchange, so the render is on us. The entry
      // being overwritten is this view's own, which is what makes the step
      // invisible to `back`.
      history.replaceState(null, '', hash);
      void this.resolve(path);
    } else location.hash = path;
  }

  get path(): string {
    return this.currentPath;
  }

  /** See ViewContext.rewrite. */
  rewrite(path: string): void {
    if (path === this.currentPath) return;
    this.currentPath = path;
    // replaceState fires neither hashchange nor popstate, which is the whole
    // point: nothing re-resolves, and the entry being overwritten is the
    // view's own.
    history.replaceState(null, '', '#' + path);
  }

  /**
   * Step back if this session has anywhere to step back to, else go to
   * `fallback`.
   *
   * The guard is what makes it safe on a cold load: history.back() from the
   * first page of a visit leaves the app entirely — on an installed PWA, it
   * closes it — so a screen opened directly by URL navigates instead.
   */
  back(fallback: string): void {
    if (this.previousPath) history.back();
    else this.navigate(fallback);
  }

  private match(path: string): { route: Route; params: Record<string, string> } | null {
    const { path: bare, query } = splitQuery(path);
    const parts = bare.split('/').filter(Boolean);
    for (const route of this.routes) {
      if (route.segments.length !== parts.length) continue;
      // Query pairs first, so a `:param` segment always wins a name clash.
      const params: Record<string, string> = { ...query };
      let ok = true;
      for (let i = 0; i < route.segments.length; i++) {
        const seg = route.segments[i];
        if (seg.startsWith(':')) params[seg.slice(1)] = decodeURIComponent(parts[i]);
        else if (seg !== parts[i]) {
          ok = false;
          break;
        }
      }
      if (ok) return { route, params };
    }
    return null;
  }

  private async resolve(path: string): Promise<void> {
    this.previousPath = this.currentPath;
    this.currentPath = path;
    const matched = this.match(path);
    if (this.teardown) {
      this.teardown();
      this.teardown = undefined;
    }
    this.outlet.replaceChildren();
    window.scrollTo(0, 0);

    if (!matched) {
      this.outlet.replaceChildren(el('p.notfound', {}, t('common.notFound')));
      return;
    }
    const ctx: ViewContext = {
      params: matched.params,
      navigate: (p) => this.navigate(p),
      from: this.previousPath,
      back: (fallback) => this.back(fallback),
      replace: (p) => this.navigate(p, true),
      rewrite: (p) => this.rewrite(p),
    };
    const result = await matched.route.view(this.outlet, ctx);
    if (typeof result === 'function') this.teardown = result;
    // Let the nav bar reflect the active tab.
    window.dispatchEvent(new CustomEvent('route', { detail: { path } }));
  }
}
